import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AddCDMReviewDialog } from "features/cdm/cdm/components/AddCDMReviewDialog/AddCDMReviewDialog";
import { CDMItemForm } from "features/cdm/cdm/components/CDMItemForm/CDMItemForm";
import { CDMPreview } from "features/cdm/cdm/components/CDMPreview/CDMPreview";
import { CarePlanViewModal } from "features/cdm/components/CarePlanViewModal/CarePlanViewModal";
import { CdmHeaderActions } from "features/cdm/components/CdmHeaderActions/CdmHeaderActions";
import { CdmHeaderBar } from "features/cdm/components/CdmHeaderBar/CdmHeaderBar";
import { CdmLoadingSkeleton } from "features/cdm/components/CdmLoadingSkeleton";
import { CdmNotices } from "features/cdm/components/CdmNotices/CdmNotices";
import { CdmSidebar } from "features/cdm/components/CdmSidebar/CdmSidebar";
import { DeregisterPatientDialog } from "features/cdm/components/DeregisterPatientDialog/DeregisterPatientDialog";
import { DeregistrationPreview } from "features/cdm/components/DeregistrationPreview/DeregistrationPreview";
import { HeldDraftNotice } from "features/cdm/components/HeldDraftNotice/HeldDraftNotice";
import { NoCdmSection } from "features/cdm/components/NoCdmSection/NoCdmSection";
import { PCRSAddProgrammeDialog } from "features/cdm/components/PCRSAddProgrammeDialog/PCRSAddProgrammeDialog";
import { PcrsCredentialsDialog } from "features/cdm/components/PcrsCredentialsDialog/PcrsCredentialsDialog";
import { PcrsDetailsDialog } from "features/cdm/components/PcrsDetailsDialog/PcrsDetailsDialog";
import { PCRSStatusBar } from "features/cdm/components/PCRSStatusBar/PCRSStatusBar";
import { ReviewsHistoryTable } from "features/cdm/components/ReviewsHistoryTable/ReviewsHistoryTable";
import {
  getPcrsErrorContent,
  PCRS_CONNECTION_REQUIRED_MESSAGE,
  PCRS_STATUS,
} from "features/cdm/constants/pcrsSchemeCheck";
import {
  isCdm,
  isDeregistration,
  isOcf,
  isPp,
} from "features/cdm/constants/reviewTypes";
import {
  useCarePlan,
  useCdmGating,
  useCdmReview,
  useCdmReviews,
  useCdmView,
  useDeregister,
  useHeldDraftRelease,
  useIsPatientDeceased,
  useOcfReview,
  usePatientCDMStatus,
  usePcrs,
  usePcrsDetails,
  usePpReview,
  useProgrammePicker,
  views,
} from "features/cdm/hooks";
import { AddOCFReviewDialog } from "features/cdm/ocf/components/AddOCFReviewDialog/AddOCFReviewDialog";
import { OCFItemForm } from "features/cdm/ocf/components/OCFItemForm/OCFItemForm";
import { OCFPreview } from "features/cdm/ocf/components/OCFPreview/OCFPreview";
import { AddPPReviewDialog } from "features/cdm/pp/components/AddPPReviewDialog/AddPPReviewDialog";
import { PPItemForm } from "features/cdm/pp/components/PPItemForm/PPItemForm";
import { PPPreview } from "features/cdm/pp/components/PPPreview/PPPreview";
import { isNonIrishForCdm } from "features/cdm/utils/cdmCountry";
import { getProgrammeTitle } from "features/cdm/utils/getProgrammeTitle";
import { getMissingHseDetails } from "features/healthlink/utils/hsePatientReadiness";
import { SimplePatientPageLayout } from "features/patients/components/SimplePatientPageLayout/SimplePatientPageLayout";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { PcrsConnectionFailed, PcrsExtensionRequired } from "features/pcrsExtension";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";

const PatientCDMPage = () => {
  const patientId = useCurrentPatientId();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Loading the CDM page (PCRS scheme check + patient details + review status)
  // can be slow. Let the user re-fetch the active queries on demand from the
  // loading skeleton instead of forcing a full page reload.
  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    try {
      await queryClient.refetchQueries({ type: "active" });
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  const { data: doctor } = useStaffInfo();
  const { data: patient } = usePatientData();
  const { data: cdmStatus } = usePatientCDMStatus(patientId);
  // PCRS: scheme check (+ BE sync), credential prompt, and cdm-ws connection.
  const {
    schemeCheck: pcrsSchemeCheck,
    isSchemeCheckLoading: isPCRSLoading,
    recheckScheme,
    isRecheckingScheme,
    cdmPatientDetails,
    isCdmPatientDetailsLoading,
    isCdmPatientDetailsPending,
    hasWsConnection,
    credentialsDialog,
  } = usePcrs({ patientId });

  const { view, setView, openReviewPreview, closeReviewPreview } =
    useCdmView(patientId);

  const {
    isPcrsAddProgrammeDialogOpen,
    preselectedReviewType,
    openProgrammePicker,
    closeProgrammePicker,
  } = useProgrammePicker();
  const { isPcrsDetailsDialogOpen, openPcrsDetails, closePcrsDetails } =
    usePcrsDetails();
  const { isDeregisterDialogOpen, openDeregister, closeDeregister } =
    useDeregister();

  const {
    temporaryOcf,
    isAddOcfReviewDialogOpen,
    setIsAddOcfReviewDialogOpen,
    pendingOcfReviewType,
    openOcfReviewDialog,
    addOcfReview,
    removeOcfReview,
  } = useOcfReview({
    patientId,
    onReviewAdded: openReviewPreview,
    onReviewRemoved: closeReviewPreview,
  });
  const {
    temporaryPp,
    isAddPpReviewDialogOpen,
    setIsAddPpReviewDialogOpen,
    pendingPpReviewType,
    openPpReviewDialog,
    addPpReview,
    removePpReview,
  } = usePpReview({
    patientId,
    onReviewAdded: openReviewPreview,
    onReviewRemoved: closeReviewPreview,
  });
  const {
    temporaryCdm,
    isAddCdmReviewDialogOpen,
    setIsAddCdmReviewDialogOpen,
    pendingCdmReviewType,
    openCdmReviewDialog,
    addCdmReview,
    removeCdmReview,
  } = useCdmReview({
    patientId,
    onReviewAdded: openReviewPreview,
    onReviewRemoved: closeReviewPreview,
  });

  const {
    visibleCDM,
    allOCF,
    allPP,
    allDeregistrations,
    activeItem,
    latestReviewId,
    mostRecentReviewIdByProgramme,
    cdmPrefillCodes,
    cdmPrefillYears,
    cdmInterimAnnualPrefillCodes,
    cdmInterimAnnualPrefillYears,
    ppPrefillCodes,
    ppPrefillYears,
    ppAnnualPrefillReasonCodes,
    ppAnnualPrefillYearOfRegistration,
    ppAnnualPrefillAdditionalRiskFactors,
    ppAnnualPrefillRiskFactorYears,
  } = useCdmReviews({
    cdmStatus,
    financialClass: patient?.financialClass,
    activeReviewId: view.cdmId,
    temporaryOcf,
    temporaryPp,
    temporaryCdm,
  });

  const { isCarePlanOpen, carePlanReviewId, openCarePlan, closeCarePlan } =
    useCarePlan({ latestReviewId });

  const isExtensionMissing =
    pcrsSchemeCheck?.status === PCRS_STATUS.EXTENSION_MISSING;
  const isPcrsUnreachable =
    pcrsSchemeCheck?.status === PCRS_STATUS.CONNECTION_ERROR;

  const {
    options,
    isPatientValid,
    isContentReady,
    currentProgramme,
    showMidMonthNotice,
    showNotOnPanelNotice,
    missingRequiredPcrsDetails,
    isMissingRequiredPcrsDetails,
    areReviewActionsDisabled,
    isEnrolled,
  } = useCdmGating({
    doctor,
    cdmStatus,
    pcrsSchemeCheck,
    cdmPatientDetails,
    hasWsConnection,
    isPCRSLoading,
    isCdmPatientDetailsPending,
  });

  // KIN-1236: a mid-month / panel-transfer review is held by the BE as a draft
  // until PCRS can verify the panel on the 1st. Surfaces the held draft and,
  // once due, re-posts the fresh scheme check so the BE can release it.
  const { heldDraft } = useHeldDraftRelease({
    patientId,
    schemeCheck: pcrsSchemeCheck,
  });

  // A deceased patient (record flagged died, or deregistered with reason "Died")
  // blocks all review actions and shows a notice.
  const isPatientDeceased = useIsPatientDeceased({
    patient,
    deregistrations: allDeregistrations,
  });
  // CDM/OCF/PP are Irish PCRS programmes — a patient whose country is explicitly
  // not Ireland is ineligible, so block review actions and show a notice.
  const isPatientNonIrish = isNonIrishForCdm(patient?.address?.country);
  // A patient may now be saved with no date of birth or gender (KIN-1733), but the CDM
  // message cannot be built without them — PID.7 and PID.8 dereference both. Blocked
  // here rather than at submit, so the GP is not four steps into a review first.
  const isMissingHseDetails =
    !!patient && getMissingHseDetails(patient).length > 0;
  const reviewActionsBlocked =
    areReviewActionsDisabled ||
    isPatientDeceased ||
    isPatientNonIrish ||
    isMissingHseDetails ||
    isMissingRequiredPcrsDetails ||
    // PCRS says the patient is not on this GP's panel — a review would be
    // rejected, so block starting/submitting entirely.
    showNotOnPanelNotice;

  return (
    <>
      <SimplePatientPageLayout
        sidebar={
          <CdmSidebar
            cdmList={visibleCDM}
            ppList={allPP}
            ocfList={allOCF}
            allowedActions={cdmStatus?.allowedActions}
            deniedActions={cdmStatus?.deniedActions}
            // Withheld while review actions are blocked (no PCRS connection, or
            // deceased patient) so the cards cannot become a way around the
            // disabled "Start review" button. Without a handler they stay
            // informational and are not clickable.
            onStartReview={reviewActionsBlocked ? undefined : openProgrammePicker}
            activeItemId={view.cdmId}
            setActiveItemId={(id) => {
              setView({ name: views.general, cdmId: id });
            }}
          />
        }
      >
        {isContentReady && isExtensionMissing && (
          <PcrsExtensionRequired onRecheck={recheckScheme} isRechecking={isRecheckingScheme} />
        )}
        {isContentReady && isPcrsUnreachable && (
          <PcrsConnectionFailed onRetry={recheckScheme} isRetrying={isRecheckingScheme} />
        )}
        {isContentReady && !isPatientValid && !isExtensionMissing && !isPcrsUnreachable && (
          <PCRSStatusBar {...getPcrsErrorContent(pcrsSchemeCheck)} />
        )}
        {!isContentReady && (
          <CdmLoadingSkeleton
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        )}
        <CdmNotices
          isContentReady={isContentReady}
          schemeCheckSkipped={
            isPatientValid && pcrsSchemeCheck?.schemeCheckPerformed === false
          }
          showMidMonthNotice={showMidMonthNotice}
          showNotOnPanelNotice={showNotOnPanelNotice}
          showDeceasedNotice={isPatientDeceased}
          showNonIrishNotice={isPatientNonIrish}
          isMissingRequiredPcrsDetails={isMissingRequiredPcrsDetails}
          missingRequiredPcrsDetails={missingRequiredPcrsDetails}
          patient={patient}
          patientId={patientId}
          isMissingHseDetails={isMissingHseDetails}
        />
        {!!heldDraft && (
          <HeldDraftNotice
            holdUntil={heldDraft.holdUntil}
            holdReason={heldDraft.holdReason}
          />
        )}
        {isContentReady && currentProgramme && (
          <CdmHeaderBar
            title={getProgrammeTitle(currentProgramme)}
            subtitle={cdmStatus?.humanReadableSummary}
            actions={
              <CdmHeaderActions
                hasProgrammeOptions={options.length > 0}
                areReviewActionsDisabled={reviewActionsBlocked}
                isPatientValid={isPatientValid}
                isEnrolled={isEnrolled}
                onStartReview={openProgrammePicker}
                onViewPcrsDetails={openPcrsDetails}
                onDeregister={openDeregister}
              />
            }
          />
        )}
        {view.name !== views.general && isContentReady && !currentProgramme && (
          <NoCdmSection
            options={options}
            cdmStatus={cdmStatus}
            isAddDisabled={reviewActionsBlocked}
            addDisabledReason={PCRS_CONNECTION_REQUIRED_MESSAGE}
            onAddCdmButtonClick={openProgrammePicker}
          />
        )}
        {view.name === views.general && (
          <>
            {activeItem?.type === "ocf" && (
              <OCFItemForm
                setView={setView}
                item={activeItem}
                patientId={patientId}
                removeItem={() => removeOcfReview(activeItem.id)}
              />
            )}
            {activeItem?.type === "pp" && (
              <PPItemForm
                setView={setView}
                item={activeItem}
                isAnnualReview={activeItem?.isAnnualReview}
                patientId={patientId}
                removeItem={() => removePpReview(activeItem.id)}
              />
            )}
            {activeItem?.type === "cdm" && (
              <CDMItemForm
                setView={setView}
                item={activeItem}
                isAnnualReview={activeItem?.isAnnualReview}
                patientId={patientId}
                type={activeItem?.reviewType}
                registrationReasons={activeItem?.registrationReasons}
                removeItem={() => removeCdmReview(activeItem.id)}
              />
            )}
            {!activeItem?.type && isCdm(activeItem?.reviewType) && (
              <CDMPreview
                item={activeItem}
                setView={setView}
                patientId={patientId}
                startEditing={!!view.startEditing}
                onOpenCarePlan={openCarePlan}
                actionsDisabled={reviewActionsBlocked}
                isMostRecent={
                  activeItem?.id === mostRecentReviewIdByProgramme.CDM?.id
                }
              />
            )}
            {!activeItem?.type && isPp(activeItem?.reviewType) && (
              <PPPreview
                item={activeItem}
                setView={setView}
                patientId={patientId}
                startEditing={!!view.startEditing}
                onOpenCarePlan={openCarePlan}
                actionsDisabled={reviewActionsBlocked}
                isMostRecent={
                  activeItem?.id === mostRecentReviewIdByProgramme.PP?.id
                }
              />
            )}
            {!activeItem?.type && isOcf(activeItem?.reviewType) && (
              <OCFPreview
                item={activeItem}
                setView={setView}
                patientId={patientId}
                startEditing={!!view.startEditing}
                actionsDisabled={reviewActionsBlocked}
              />
            )}
            {!activeItem?.type && isDeregistration(activeItem?.reviewType) && (
              <DeregistrationPreview item={activeItem} setView={setView} />
            )}
          </>
        )}
        {view.name !== views.general && isContentReady && (
          <ReviewsHistoryTable
            reviews={cdmStatus?.reviewHistory ?? []}
            patientId={patientId}
            actionsDisabled={reviewActionsBlocked}
            onOpenPreview={(id) => setView({ name: views.general, cdmId: id })}
            onOpenEdit={(id) =>
              setView({ name: views.general, cdmId: id, startEditing: true })
            }
            onOpenCarePlan={openCarePlan}
          />
        )}
      </SimplePatientPageLayout>

      <PcrsCredentialsDialog {...credentialsDialog} />

      <PcrsDetailsDialog
        open={isPcrsDetailsDialogOpen}
        onClose={closePcrsDetails}
        schemeCheck={pcrsSchemeCheck}
        patientDetails={cdmPatientDetails}
        isPatientDetailsLoading={isCdmPatientDetailsLoading}
      />

      <DeregisterPatientDialog
        isOpen={isDeregisterDialogOpen}
        onClose={closeDeregister}
        patientId={patientId}
      />

      <CarePlanViewModal
        isOpen={isCarePlanOpen}
        onClose={closeCarePlan}
        patientId={patientId}
        reviewId={carePlanReviewId}
      />

      <PCRSAddProgrammeDialog
        isPcrsAddProgrammeDialogOpen={isPcrsAddProgrammeDialogOpen}
        preselectedReviewType={preselectedReviewType}
        setIsPcrsAddProgrammeDialogOpen={closeProgrammePicker}
        onAddOcfButtonClick={openOcfReviewDialog}
        onAddPpButtonClick={openPpReviewDialog}
        onAddCdmButtonClick={openCdmReviewDialog}
        allowedActions={cdmStatus?.allowedActions}
        deniedActions={cdmStatus?.deniedActions}
      />
      <AddOCFReviewDialog
        isAddOcfReviewDialogOpen={isAddOcfReviewDialogOpen}
        setIsAddOcfReviewDialogOpen={setIsAddOcfReviewDialogOpen}
        reviewType={pendingOcfReviewType}
        addNewOCFItem={addOcfReview}
        doctor={doctor}
      />
      <AddPPReviewDialog
        isAddPpReviewDialogOpen={isAddPpReviewDialogOpen}
        setIsAddPpReviewDialogOpen={setIsAddPpReviewDialogOpen}
        reviewType={pendingPpReviewType}
        prefillReasonCodes={ppPrefillCodes}
        prefillReasonYears={ppPrefillYears}
        annualPrefillReasonCodes={ppAnnualPrefillReasonCodes}
        annualPrefillYearOfRegistration={ppAnnualPrefillYearOfRegistration}
        annualPrefillAdditionalRiskFactors={ppAnnualPrefillAdditionalRiskFactors}
        annualPrefillRiskFactorYears={ppAnnualPrefillRiskFactorYears}
        addNewPPItem={addPpReview}
        doctor={doctor}
      />
      <AddCDMReviewDialog
        isAddCdmReviewDialogOpen={isAddCdmReviewDialogOpen}
        setIsAddCdmReviewDialogOpen={setIsAddCdmReviewDialogOpen}
        reviewType={pendingCdmReviewType}
        prefillDiagnosisCodes={cdmPrefillCodes}
        prefillDiagnosisYears={cdmPrefillYears}
        interimAnnualPrefillCodes={cdmInterimAnnualPrefillCodes}
        interimAnnualPrefillYears={cdmInterimAnnualPrefillYears}
        addNewCDMItem={addCdmReview}
        doctor={doctor}
      />
    </>
  );
};

export { PatientCDMPage };
